import { comparisonManager, comparisonService } from '@/engines/comparison'
import type { ComparisonOffer } from '@/engines/comparison'
import { budgetEditorAdapter } from '@/modules/budget-editor-integration'
import { quoteExporter } from './QuoteExporter'
import { quoteHistory } from './QuoteHistory'
import { quoteTemplates } from './QuoteTemplates'
import { quoteValidator } from './QuoteValidator'
import type {
  ComparisonProductSource,
  QuoteDocumentPro,
  QuoteSidebarCategory,
} from './QuoteTypes'
import { SERVICE_CATALOG } from './QuoteTypes'

/**
 * QuoteManager — orchestrates Quote Builder PRO + Comparison Engine intake.
 * Document money-state mutations go through BudgetEditorAdapter → Budget Engine Commands.
 */
export class QuoteManager {
  private favorites: string[] = []

  getDocument() {
    budgetEditorAdapter.ensureDocument('Novo Orçamento PRO')
    return budgetEditorAdapter.getQuoteDocument()
  }

  newDocument(title?: string) {
    const doc = budgetEditorAdapter.createDocument(title)
    quoteHistory.autosave(doc)
    return doc
  }

  setDocument(doc: QuoteDocumentPro) {
    const next = budgetEditorAdapter.syncFromQuoteDocument(doc)
    quoteHistory.autosave(next)
    return next
  }

  /** Pull selected offers from Unified Comparison Engine. */
  addSelectedFromComparison() {
    const offers = comparisonManager.getSelectedOffers()
    for (const offer of offers) {
      this.addComparisonOffer(offer as ComparisonOffer)
    }
    return this.getDocument()
  }

  addComparisonOffer(offer: ComparisonOffer) {
    const source: ComparisonProductSource = {
      productId: offer.product.id,
      fingerprint: offer.fingerprint,
      description: `${offer.product.brand} ${offer.product.model} ${offer.product.capacity}`.trim(),
      purchasePrice: offer.purchasePrice ?? round2(offer.price * 0.82),
      salePrice: offer.currency === 'USD' ? round2(offer.price * 5.4) : offer.price,
      supplierId: offer.supplierId,
      supplierName: offer.supplierName,
      comparisonOfferId: offer.offerId,
      category: offer.product.category,
    }
    const doc = budgetEditorAdapter.addFromComparison(source)
    quoteHistory.autosave(doc)
    return doc
  }

  /**
   * Search comparison catalog by category/term — never free-text product creation.
   */
  searchComparison(term: string, category?: QuoteSidebarCategory) {
    const result = comparisonService.loadFromSearchCatalog(term)
    const rows = result.rows.filter((row) => {
      if (!category || category === 'Favoritos' || category === 'Histórico' || category === 'Serviços') {
        return true
      }
      return row.product.category.toLowerCase().includes(category.toLowerCase()) ||
        categoryMatches(row.product.category, category)
    })
    return rows
  }

  addService(serviceId: string) {
    const doc = budgetEditorAdapter.addService(serviceId)
    quoteHistory.autosave(doc)
    return doc
  }

  applyTemplate(templateId: string) {
    const template = quoteTemplates.get(templateId)
    if (!template) throw new Error('Template não encontrado')

    for (const item of template.blocks) {
      if (item.type === 'product') {
        budgetEditorAdapter.addFromComparison({
          productId: item.productId,
          fingerprint: item.fingerprint ?? `TEMPLATE-${item.productId}`,
          description: item.description,
          purchasePrice: item.purchasePrice ?? 0,
          salePrice: item.salePrice ?? 0,
          supplierId: 'template',
          supplierName: 'TEMPLATE',
          comparisonOfferId: `template:${item.productId}`,
        })
      } else if (item.type === 'service' || item.type === 'labor') {
        budgetEditorAdapter.addService(item.productId)
      } else {
        budgetEditorAdapter.addStructural(item.type, item.description, item.productId)
      }
    }
    const doc = this.getDocument()
    quoteHistory.autosave(doc)
    return doc
  }

  updateBlock(blockId: string, patch: Parameters<typeof budgetEditorAdapter.updateBlock>[1]) {
    const doc = budgetEditorAdapter.updateBlock(blockId, patch)
    quoteHistory.autosave(doc)
    return doc
  }

  removeBlock(blockId: string) {
    const doc = budgetEditorAdapter.removeBlock(blockId)
    quoteHistory.autosave(doc)
    return doc
  }

  duplicateBlock(blockId: string) {
    const doc = budgetEditorAdapter.duplicateBlock(blockId)
    quoteHistory.autosave(doc)
    return doc
  }

  moveBlock(from: number, to: number) {
    const doc = budgetEditorAdapter.moveBlock(from, to)
    quoteHistory.autosave(doc)
    return doc
  }

  setGlobalMargin(percent: number | null) {
    const doc = budgetEditorAdapter.setGlobalMargin(percent)
    quoteHistory.autosave(doc)
    return doc
  }

  setGlobalDiscount(percent: number, value: number) {
    const doc = budgetEditorAdapter.setGlobalDiscount(percent, value)
    quoteHistory.autosave(doc)
    return doc
  }

  save() {
    const document = this.getDocument()
    const validation = quoteValidator.validateDocument(document)
    if (!validation.valid) throw new Error(validation.errors.join('; '))
    return quoteHistory.save(document)
  }

  load(id: string) {
    const doc = quoteHistory.load(id)
    if (!doc) throw new Error('Orçamento não encontrado')
    const next = budgetEditorAdapter.syncFromQuoteDocument(doc)
    return next
  }

  restoreAutosave() {
    const doc = quoteHistory.loadAutosave()
    if (doc) return budgetEditorAdapter.syncFromQuoteDocument(doc)
    return this.getDocument()
  }

  listHistory() {
    return quoteHistory.list()
  }

  listServices() {
    return SERVICE_CATALOG
  }

  listTemplates(category?: QuoteSidebarCategory) {
    return category ? quoteTemplates.byCategory(category) : quoteTemplates.list()
  }

  exportPdf() {
    return quoteExporter.preparePdf(this.getDocument())
  }

  /** Sprint 3.0 — apply commercial template via Budget Commands (no UI rules). */
  applyCommercialTemplate(templateId: string) {
    const doc = budgetEditorAdapter.applyCommercialTemplate(templateId)
    quoteHistory.autosave(doc)
    return doc
  }

  toggleFavorite(fingerprint: string) {
    if (this.favorites.includes(fingerprint)) {
      this.favorites = this.favorites.filter((f) => f !== fingerprint)
    } else {
      this.favorites = [...this.favorites, fingerprint]
    }
    return this.favorites
  }

  getFavorites() {
    return this.favorites
  }
}

function categoryMatches(productCategory: string, sidebar: QuoteSidebarCategory): boolean {
  const map: Record<string, string[]> = {
    Desktop: ['Desktop', 'Gabinete', 'Fonte', 'CPU', 'GPU', 'RAM', 'SSD', 'Placa Mãe'],
    Notebook: ['Notebook'],
    Servidor: ['Servidor'],
    Rede: ['Switch', 'Firewall', 'Access Point', 'Roteador'],
    Monitor: ['Monitor'],
    Impressora: ['Impressora'],
    Periféricos: ['Teclado', 'Mouse', 'Headset'],
  }
  return (map[sidebar] ?? []).some((c) => c.toLowerCase() === productCategory.toLowerCase())
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const quoteManager = new QuoteManager()
